import { defineComponent, ref, Ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { Listing, ListingTemplate, PreferenceType } from '@truckdown/locations';
import context from '@truckdown/components-systems';
import { LocationUtilities } from '../../../LocationUtilities';
import H from '@here/maps-api-for-javascript';

interface IData {
    bubbleMounted: Ref<boolean>,
    fullscreenLink: Ref<HTMLElement | null>,
    mapContainer: Ref<HTMLElement | null>,
    locationMarker: Ref<H.map.Marker | null>,
    bubbleEl: Ref<HTMLDivElement | null>
}

const getInitialData = function (props: any): IData {
    return {
        mapContainer: ref<HTMLElement | null>(null),
        fullscreenLink: ref<HTMLElement | null>(null),
        locationMarker: ref<H.map.Marker | null>(null),
        bubbleMounted: ref<boolean>(false),
        bubbleEl: ref<HTMLDivElement | null>(null)
    };
};

export default defineComponent({
    name: 'listing-map',
    props: {
        listing: {
            type: Object as () => Listing,
            required: true
        }
    },
    emits: [],
    setup: function (props, ctx) {
        let hMap: H.Map | null = null;
        let data = getInitialData(props);

        let platform: H.service.Platform = new H.service.Platform({
            apikey: context.settings['here-key']
        });
        const defaultLayers = platform.createDefaultLayers();

        const center = computed<H.geo.IPoint>(() => ({
            lat: props.listing.location.lat,
            lng: props.listing.location.lng
        }));

        const icon = new H.map.Icon(LocationUtilities.getIcon(props.listing, false));
        const selectedIcon = new H.map.Icon(LocationUtilities.getIcon(props.listing, true));

        let marker: H.map.Marker = new H.map.Marker(
            center.value,
            {
                volatility: true,
                data: 'Marker data',
                icon: icon
            }
        );
        let bubble: H.ui.InfoBubble | null = null;



        const openBubble = function (ui: H.ui.UI, position: { lat: number; lng: number }) {
            data.bubbleMounted.value = true;
            nextTick(() => {
                
                if (!data.bubbleEl.value) return;
                if (bubble) ui.removeBubble(bubble);

                bubble = new H.ui.InfoBubble(
                   getBubblePoint(position),
                    { content: data.bubbleEl.value } 
                );




                if (bubble) {

                    const stateChange = function (evt: any) {
                        if (evt.target.getState() === H.ui.InfoBubble.State.CLOSED) {
                            marker.setIcon(icon);
                        }
                    };

                    bubble.addEventListener('statechange', stateChange);
                    marker.setIcon(selectedIcon);
                    ui.addBubble(bubble);
                }
                
            });
        }

        const getBubblePoint = function (point: { lat: number; lng: number }) {
            if (hMap) {
                var screenOffset = hMap.geoToScreen(point);
                if (screenOffset) {
                    var coord = hMap.screenToGeo(screenOffset.x, screenOffset.y - 47);
                    if (coord) {
                        return coord;
                    }
                }
            }

            return point;
        }

        const addLocationMarker = async function (map: H.Map, ui: H.ui.UI) {
            map.addObject(marker);
            data.locationMarker.value = marker;
            map.getViewPort().resize();
            marker.addEventListener('tap', () => {
                openBubble(ui, center.value,
                );
            });
        
            marker.addEventListener("pointerenter", () => {
            map.getElement().style.cursor = "pointer";
            });

            marker.addEventListener("pointerleave", () => {
            map.getElement().style.cursor = "";
            });
        };


        const toggleFullScreen = async () => {
          const el = data.mapContainer.value as HTMLElement | null;
          if (!el) return;

          try {
            if (document.fullscreenElement === el) {
              await document.exitFullscreen();
              return;
            }

            if (el.requestFullscreen) {
              await el.requestFullscreen({ navigationUI: "show" } as any);
            }
          } catch (err) {
            console.warn("Fullscreen failed:", err);
          }
        };

        const addMapLayerButton = function(ui: H.ui.UI) {
            ui.removeControl("mapsettings");
            ui.addControl("mapsettings", new H.ui.MapSettingsControl({
                baseLayers: [
                    { label: "Map", layer: defaultLayers.vector.normal.map },
                    { label: "Satellite", layer: defaultLayers.raster.satellite.map }
                ]
            }));

            const scalebar = ui.getControl("scalebar");
            if (scalebar) {
                ui.removeControl("scalebar");
                ui.addControl("scalebar", scalebar);
            }
        };

        const addFullscreenButton = function(ui: H.ui.UI) {
             const fsContainer = new H.ui.Control();
            fsContainer.setAlignment(H.ui.LayoutAlignment.TOP_RIGHT);
            const fsButton = new H.ui.base.Element(
                "button",
                "td-btn icon-only"
            );

            fsContainer.addChild(fsButton);

            ui.addControl("fullscreen", fsContainer);

            requestAnimationFrame(() => {
                const btn = fsButton.getElement() as HTMLButtonElement;
                if (!btn) return;

                btn.type = "button";
                btn.style.padding = "0";
                btn.style.fontSize = "18px";
                btn.style.fontWeight = "bold";
                btn.style.backgroundColor = "#fff";
           
                btn.innerHTML = "&#x26F6;";

                btn.addEventListener("click", (e) => {
                e.preventDefault();
                toggleFullScreen();
                hMap?.getViewPort().resize();
                });
            });
        }

        const setupDefaultMap = async function () {
            if (!data.mapContainer.value) {
                console.error("Map container is not ready yet");
                return;
            }

            let map = new H.Map(
                data.mapContainer.value,
                defaultLayers.vector.normal.map,
                {
                    center: center.value,
                    zoom: 14,
                    pixelRatio: window.devicePixelRatio || 1
                }
            );

            hMap = map;
            if (map) {
                let behaviour = new H.mapevents.Behavior(new H.mapevents.MapEvents(map));
                await nextTick();
                map.getViewPort().resize();
                window.addEventListener('resize', () => map.getViewPort().resize());
               
                try {
                    const ui = H.ui.UI.createDefault(map, defaultLayers);

                    await addLocationMarker(map, ui);
                    addMapLayerButton(ui);
                    addFullscreenButton(ui);
                    addCircleToMap(map);

                } catch (e) {
                    console.error("HERE UI init failed:", e);
                }
            }
        }

        const addCircleToMap = function (map: H.Map) {
            const circle = new H.map.Circle(center.value, 24140.16);
            circle.setStyle(
                new H.map.SpatialStyle({
                    strokeColor: "rgba(86, 89, 97, 1)",
                    fillColor: "rgba(0, 0, 0, 0.05)",
                    lineWidth: 2
                })
            );
            map.addObject(circle);
        };

        const handleFullScreenChange = function () {
            const el = data.mapContainer.value as HTMLElement | null;
            if (!el) return;
            const isFs = document.fullscreenElement === el;

            if (!isFs) {
            el.style.height = "320px";
            el.style.width = "auto";
            }

            hMap?.getViewPort().resize(); // IMPORTANT for HEREmaps
        };

        onMounted(async () => {
            setupDefaultMap();
            document.addEventListener("fullscreenchange", handleFullScreenChange);
        });

        onUnmounted(() => {
            document.removeEventListener("fullscreenchange", handleFullScreenChange);
        });
       
        return {
            ...data,
            center,
            icon
        };
    }
});
